import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";
import { refundTransactionBank, refundTransactionService } from "../../services/TransactionService";
import "./AdminModalForm.css";

const AdminBulkRefundModal = ({ isOpen, onClose, selectedIds, selectedTransactions = [], onComplete }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    action: "bank",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (isOpen) {
      setFormData({ action: "bank" });
      setSaving(false);
      setError("");
      setSuccess("");
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const isEligibleForAction = (transaction, action) => {
    if (!transaction) return false;
    if (transaction.success !== true) return false;
    if (transaction.promocode_code !== null) return false;

    if (action === "wallet") {
      return transaction.transaction_type === "service";
    }

    if (action === "bank") {
      return transaction.transaction_type === "topup";
    }

    return false;
  };

  const getInvalidReasons = (transaction, action) => {
    const reasons = [];

    if (transaction.success !== true) {
      reasons.push("success must be true");
    }

    if (transaction.promocode_code !== null) {
      reasons.push("promocode must be null");
    }

    if (action === "wallet" && transaction.transaction_type !== "service") {
      reasons.push("type must be service");
    }

    if (action === "bank" && transaction.transaction_type !== "topup") {
      reasons.push("type must be topup");
    }

    return reasons;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!selectedTransactions?.length) {
      setError(t("errors.selectAtLeastOneTransaction", "Please select at least one transaction."));
      return;
    }

    const validTransactions = selectedTransactions.filter((transaction) =>
      isEligibleForAction(transaction, formData.action),
    );

    const invalidTransactions = selectedTransactions.filter(
      (transaction) => !isEligibleForAction(transaction, formData.action),
    );

    if (!validTransactions.length) {
      setError(
        [
          "No selected transactions are eligible for this action.",
          "",
          ...invalidTransactions.map(
            (transaction) =>
              `- Transaction ${transaction.id}: ${getInvalidReasons(transaction, formData.action).join(", ")}`,
          ),
        ].join("\n"),
      );
      return;
    }

    try {
      setSaving(true);

      const refundAction = formData.action === "bank" ? refundTransactionBank : refundTransactionService;

      let successes = 0;
      let failures = 0;
      const failureDetails = [];

      for (const transaction of validTransactions) {
        try {
          await refundAction(transaction.id);
          successes++;
        } catch (err) {
          failures++;
          failureDetails.push(`- Transaction ${transaction.id}: ${err.message || "Refund failed"}`);
        }
      }

      const skippedDetails = invalidTransactions.map(
        (transaction) =>
          `- Transaction ${transaction.id}: skipped (${getInvalidReasons(transaction, formData.action).join(", ")})`,
      );

      if (failures > 0 || invalidTransactions.length > 0) {
        setError(
          [
            "Completed with errors.",
            `Success: ${successes}`,
            `Failed: ${failures}`,
            `Skipped: ${invalidTransactions.length}`,
            "",
            ...failureDetails,
            ...skippedDetails,
          ].join("\n"),
        );
      } else {
        setSuccess(`Bulk action completed successfully for ${successes} transaction(s).`);
      }

      // onComplete?.(successes, failures);
    } catch (err) {
      setError(err.message || "Failed to process bulk action.");
    } finally {
      setSaving(false);
    }
  };
  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title="Bulk Action"
      actions={
        <>
          <SecondButton onClick={onClose} disabled={saving}>
            Cancel
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={saving}>
            {saving ? "Processing..." : "Apply Action"}
          </MainButton>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="admin-modal-form">
        <label>Selected Transactions: {selectedIds?.length || 0}</label>

        <label>
          Action *
          <div className="admin-modal-select-wrapper">
            <select
              name="action"
              value={formData.action}
              onChange={handleChange}
              disabled={saving}
              className="admin-modal-select admin-modal-select-with-arrow"
            >
              <option value="bank">Refund request to Bank</option>
              <option value="wallet">Refund request to User Wallet</option>
            </select>
          </div>
        </label>

        {error && (
          <div className="admin-modal-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {success && (
          <div className="admin-modal-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default AdminBulkRefundModal;
